import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  configurationService,
  AISettings as AISettingsType,
} from '../../services/configurationService';
import { ApiError } from '../../services/apiClient';

interface AISettingsProps {
  /** Opens the CAD Records tab, where a broken scoring category is corrected. */
  onOpenCadSettings?: () => void;
}

/**
 * Renaming or removing a rubric category can orphan a category the CAD Records tab lists in
 * CadRescoreCategories: that category silently stops scoring, and the only other trace is a log
 * line when the next CAD record arrives. The rubric is the more important object, so the save is
 * accepted and what it broke is reported here, to the person who just caused it, rather than
 * refused or left for whoever next opens the CAD tab.
 */
export const AISettings: React.FC<AISettingsProps> = ({ onOpenCadSettings }) => {
  const [settings, setSettings] = useState<AISettingsType>({
    summarizationPrompt: '',
    analysisPrompt: '',
    chatbotSystemPrompt: '',
    chatbotMaxResults: 5,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [helpDialog, setHelpDialog] = useState<string | null>(null);
  const [staleCadCategories, setStaleCadCategories] = useState<string[]>([]);
  /** What the tenant has stored, so a save sends only what the admin actually changed. */
  const [baseline, setBaseline] = useState<AISettingsType | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const aiSettings = await configurationService.getAISettings();
      setSettings(aiSettings);
      setBaseline(aiSettings);
      setError('');
    } catch (_err) {
      setError('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const result = await configurationService.updateAISettings(settings, baseline ?? undefined);
      // The saved values are now the stored values, so a second Save is correctly a no-op.
      setBaseline(settings);
      setSuccess('AI settings updated successfully');
      setError('');
      // Not cleared on a timer like the success message: this one is a break to act on, and it
      // stays until the next save or until it is dismissed.
      setStaleCadCategories(result.staleCadRescoreCategories);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      // Surface the server's own message: it names the key it refused and why, which "Failed to
      // update" does not. A cleared prompt box is the likeliest refusal on this tab.
      setError((err as ApiError)?.message || 'Failed to update AI settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
    setError('');
    setSuccess('');
    setStaleCadCategories([]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        AI Configuration — Tenant Prompts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Configure system prompts for call analysis and chatbot. These are tenant-specific.
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Model parameters (Temperature, MaxTokens, TopP, etc.) are managed at the platform level
        by PlatformAdmin via Platform Settings.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {staleCadCategories.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setStaleCadCategories([])}>
          <AlertTitle>
            {staleCadCategories.length === 1
              ? '1 CAD scoring category no longer matches this rubric'
              : `${staleCadCategories.length} CAD scoring categories no longer match this rubric`}
          </AlertTitle>
          The rubric was saved. {staleCadCategories.join(', ')}{' '}
          {staleCadCategories.length === 1 ? 'is' : 'are'} still listed on the CAD Records tab, and
          the CAD record will not score {staleCadCategories.length === 1 ? 'it' : 'them'} until the
          names match. Correct the list there, or rename the {staleCadCategories.length === 1 ? 'category' : 'categories'} back in this rubric.
          {onOpenCadSettings && (
            <>
              {' '}
              <Link component="button" type="button" onClick={onOpenCadSettings}>
                Open the CAD Records tab
              </Link>
            </>
          )}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>System Prompts</Typography>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
                <Typography sx={{ flexGrow: 1 }}>Call Summarization Prompt</Typography>
                <Tooltip title="View example prompt">
                  <Box
                    component="span"
                    onClick={(e) => { e.stopPropagation(); setHelpDialog('summarization'); }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer', color: 'action.active' }} />
                  </Box>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                value={settings.summarizationPrompt}
                onChange={(e) => setSettings((prev) => ({ ...prev, summarizationPrompt: e.target.value }))}
                multiline
                rows={8}
                fullWidth
                placeholder="Enter the system prompt for call summarization..."
                helperText="This prompt guides the AI when creating call summaries (gpt-5-nano)"
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
                <Typography sx={{ flexGrow: 1 }}>Quality Analysis Prompt</Typography>
                <Tooltip title="View example prompt">
                  <Box
                    component="span"
                    onClick={(e) => { e.stopPropagation(); setHelpDialog('analysis'); }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer', color: 'action.active' }} />
                  </Box>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Response format is handled automatically. Focus on defining categories, criteria,
                  weights, and evaluation guidelines.
                </Typography>
              </Alert>
              <TextField
                value={settings.analysisPrompt}
                onChange={(e) => setSettings((prev) => ({ ...prev, analysisPrompt: e.target.value }))}
                multiline
                rows={12}
                fullWidth
                placeholder="Enter the system prompt for quality analysis..."
                helperText="Define what to evaluate and how to score (gpt-oss-120b)"
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
                <Typography sx={{ flexGrow: 1 }}>Chatbot System Prompt</Typography>
                <Tooltip title="View example prompt">
                  <Box
                    component="span"
                    onClick={(e) => { e.stopPropagation(); setHelpDialog('chatbot'); }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer', color: 'action.active' }} />
                  </Box>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                value={settings.chatbotSystemPrompt}
                onChange={(e) => setSettings((prev) => ({ ...prev, chatbotSystemPrompt: e.target.value }))}
                multiline
                rows={6}
                fullWidth
                placeholder="Enter the system prompt for the CallScore Assistant..."
                helperText="This prompt defines the chatbot's personality and capabilities"
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Additional Configurations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="Chatbot Max Results"
                type="number"
                value={settings.chatbotMaxResults}
                onChange={(e) => setSettings((prev) => ({ ...prev, chatbotMaxResults: parseInt(e.target.value) || 5 }))}
                inputProps={{ min: 1, max: 50 }}
                helperText="Maximum number of search results for chatbot RAG queries (1-50)"
              />
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Settings'}
        </Button>
        <Button variant="outlined" onClick={handleReset} disabled={saving}>
          Reset
        </Button>
      </Box>

      {/* Help Dialog */}
      <Dialog open={helpDialog !== null} onClose={() => setHelpDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {helpDialog === 'analysis' && 'Quality Analysis Prompt Example'}
          {helpDialog === 'summarization' && 'Call Summarization Prompt Example'}
          {helpDialog === 'chatbot' && 'Chatbot System Prompt Example'}
        </DialogTitle>
        <DialogContent>
          {helpDialog === 'analysis' && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Response format is automatically handled. Focus on defining categories, criteria,
                  and evaluation guidelines.
                </Typography>
              </Alert>
              <Typography variant="body2" component="pre"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`You are a quality assurance expert for emergency dispatch services.

Categories to evaluate:
1. CaseEntry (Weight: 15)
   - Location Verification (Weight: 5)
   - Callback Number (Weight: 5)
   - Caller Information (Weight: 5)

2. CADSkills (Weight: 20)
   - Incident Classification (Weight: 7)
   - Documentation (Weight: 6)
   - Software Proficiency (Weight: 7)

3. CustomerService (Weight: 25)
   - Communication Clarity (Weight: 8)
   - Active Listening (Weight: 6)
   - Call Control (Weight: 6)
   - Empathy (Weight: 5)

4. QAEOverview (Weight: 40)
   - Protocol Adherence (Weight: 10)
   - Judgment (Weight: 10)
   - Situational Awareness (Weight: 10)
   - Problem Solving (Weight: 10)

Scoring: Score each criterion from 0 to its weight value.`}
              </Typography>
            </Box>
          )}
          {helpDialog === 'summarization' && (
            <Typography variant="body2" component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`You are an expert at summarizing emergency dispatch calls.

Guidelines:
- Focus on key facts: incident type, location, parties, actions taken
- Use clear, professional language
- Include priority level and outcome when available

Format response as JSON:
{
  "caseType": "Emergency type",
  "summary": "Brief description",
  "location": "Address or location",
  "priority": "High, Medium, Low",
  "outcome": "Resolution or status"
}`}
            </Typography>
          )}
          {helpDialog === 'chatbot' && (
            <Typography variant="body2" component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`You are CallScore Assistant, an AI assistant for the GovIQ CallScore platform.
You help users with questions about training materials, standards, and platform usage.

Guidelines:
- Provide accurate, helpful responses based on context provided
- Keep responses concise but comprehensive
- Use a professional but friendly tone
- Reference specific training materials when applicable`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
